'use client';

// Deep-import the concrete module, not the `@naumu/shared` barrel - the barrel
// re-exports every schemas/*.ts and drags zod (~299 KiB) into any chunk that
// touches it, including the marketing shell's.
import { isHeicType, resolveFileMimeType } from '@naumu/shared/types/attachment';

export interface ImagePreview {
	/**
	 * Object URL for something the browser can actually paint. The caller owns
	 * it and must `URL.revokeObjectURL` when the preview goes away - exactly as
	 * it would for a plain `URL.createObjectURL(file)`.
	 */
	url: string;
	/** Natural pixel size, when the browser managed to decode the preview. */
	width?: number;
	height?: number;
}

export interface ImagePreviewHandle {
	/** Object URL over the original bytes. Available synchronously. */
	url: string;
	/**
	 * Natural size, once the browser has decoded the image. Resolves empty
	 * rather than rejecting, so a decode failure just leaves the size unknown.
	 */
	dimensions: Promise<{ width?: number; height?: number }>;
}

/** Matches the composer's other client-side transcodes (video/PDF posters). */
const HEIC_JPEG_QUALITY = 0.85;

/** Reads natural dimensions off a URL, resolving empty rather than rejecting. */
function readDimensions(url: string): Promise<{ width?: number; height?: number }> {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
		img.onerror = () => resolve({});
		img.src = url;
	});
}

/**
 * True when the file is HEIC/HEIF - the iPhone camera default, which only
 * Safari can decode. Reads the extension when the browser left `File.type`
 * blank, which it routinely does for HEIC outside Safari.
 *
 * Callers branch on this because the two cases have different *timing*: every
 * other image has a paintable object URL immediately, HEIC has none until it
 * has been transcoded.
 */
export function isHeicFile(file: File): boolean {
	return isHeicType(resolveFileMimeType(file.name, file.type));
}

/**
 * Immediate, paintable preview for a non-HEIC image.
 *
 * Synchronous on purpose: the object URL is what the composer chip renders the
 * instant a file is dropped. Awaiting a decode before showing anything made
 * every image - not just HEIC - paint late. Dimensions arrive separately and
 * are patched in when they land.
 *
 * Do NOT call this for a HEIC file: outside Safari the resulting URL paints
 * nothing. Gate on `isHeicFile` and use `transcodeHeicPreview` instead.
 */
export function createImagePreview(file: File): ImagePreviewHandle {
	const url = URL.createObjectURL(file);
	return { url, dimensions: readDimensions(url) };
}

/**
 * Renderable preview for a HEIC/HEIF file.
 *
 * HEIC is the iPhone camera default and only Safari can decode it, so Chrome
 * and Firefox render nothing from the raw blob - no preview, and no dimensions
 * either, because `new Image()` errors on it. We transcode to JPEG with
 * libheif (lazily imported: the decoder is ~3 MB and must never land in the
 * main bundle) and hand back a URL over the transcode.
 *
 * A failed transcode falls back to the raw object URL rather than to nothing:
 * Safari paints HEIC natively, so the fallback is the correct preview there.
 * Either way the caller owns the returned URL and must revoke it.
 *
 * Only the pre-upload path needs this. Once a file is in S3 the download
 * endpoint serves a browser-renderable webp transcode, so every `<img>` on a
 * stored attachment already works.
 */
export async function transcodeHeicPreview(file: File): Promise<ImagePreview> {
	try {
		const { heicTo } = await import('heic-to');
		const jpeg = await heicTo({ blob: file, type: 'image/jpeg', quality: HEIC_JPEG_QUALITY });
		const url = URL.createObjectURL(jpeg);
		return { url, ...(await readDimensions(url)) };
	} catch (err) {
		console.error(`[image-preview] HEIC transcode failed for ${file.name}:`, err);
	}

	const url = URL.createObjectURL(file);
	return { url, ...(await readDimensions(url)) };
}
